import { getAudioContext, resumeAudio } from './webAudio';

/**
 * THE APP'S SOUNDS - composed here, rendered once, played from memory.
 *
 * Every sound used to be a bare sine wave switched on and off with a straight
 * line: a laboratory beep, and it sounded like one. These are INSTRUMENTS -
 * each note is a stack of tuned partials, each partial with its own natural
 * decay (the highs die first, as they do on a struck bar), passed through a
 * gentle low-pass, a short synthetic room and a compressor that keeps the peak
 * polite. Nothing is sampled and nothing is copied: plain intervals, generated
 * at runtime, with no audio asset to license.
 *
 * RENDERED OFFLINE, ONCE. A sound is built in an `OfflineAudioContext`, cached
 * as an `AudioBuffer`, and every later play is a buffer source - no oscillator
 * graph is rebuilt per message, and a ringtone is exactly one cycle long so it
 * loops without a seam.
 */

type Voice = 'mallet' | 'bell' | 'glass';

interface Partial {
  /** Frequency as a multiple of the note's fundamental. */
  readonly ratio: number;
  readonly amp: number;
  /** Time constant of the exponential decay, in seconds. */
  readonly decay: number;
}

/*
 * THE THREE VOICES.
 *   mallet - a tuned wooden bar: the 1 : 3.9 : 9.2 overtones of a marimba key,
 *            warm and short. Messages, the call-ended cadence.
 *   bell   - inharmonic partials that ring on: ringtones, the "you have a call"
 *            colour.
 *   glass  - nearly harmonic and bright, used quietly as a sparkle on top.
 */
const VOICES: Readonly<Record<Voice, { readonly attack: number; readonly partials: readonly Partial[] }>> = {
  mallet: {
    attack: 0.003,
    partials: [
      { ratio: 1, amp: 1, decay: 0.38 },
      { ratio: 3.93, amp: 0.2, decay: 0.09 },
      { ratio: 9.24, amp: 0.045, decay: 0.03 },
    ],
  },
  bell: {
    attack: 0.002,
    partials: [
      { ratio: 1, amp: 1, decay: 0.95 },
      { ratio: 2.02, amp: 0.38, decay: 0.55 },
      { ratio: 2.76, amp: 0.24, decay: 0.38 },
      { ratio: 5.4, amp: 0.1, decay: 0.16 },
      { ratio: 8.93, amp: 0.04, decay: 0.07 },
    ],
  },
  glass: {
    attack: 0.004,
    partials: [
      { ratio: 1, amp: 1, decay: 0.55 },
      { ratio: 2, amp: 0.28, decay: 0.32 },
      { ratio: 3.01, amp: 0.12, decay: 0.18 },
    ],
  },
};

interface Note {
  /** Seconds from the start of the sound. */
  readonly t: number;
  /** MIDI note number (69 = A4). */
  readonly midi: number;
  readonly voice: Voice;
  readonly gain: number;
  /** Stereo position, -1 .. 1. */
  readonly pan?: number;
}

interface SoundSpec {
  /** Rendered length; for a ringtone, one cycle - it loops at exactly this. */
  readonly length: number;
  readonly notes: readonly Note[];
  /** Playback level after normalising the render to a fixed peak. */
  readonly volume: number;
  /** Low-pass corner, Hz. Lower is warmer. */
  readonly tone?: number;
  /** Reverb send, 0 .. 1. */
  readonly room?: number;
}

const m = (t: number, midi: number, gain = 0.85, pan = 0): Note => ({ t, midi, voice: 'mallet', gain, pan });
const b = (t: number, midi: number, gain = 0.6, pan = 0): Note => ({ t, midi, voice: 'bell', gain, pan });
const g = (t: number, midi: number, gain = 0.12, pan = 0): Note => ({ t, midi, voice: 'glass', gain, pan });

export type SoundId = 'message' | 'callEnded' | 'join' | 'leave' | 'ringback';

const SOUNDS: Readonly<Record<SoundId, SoundSpec>> = {
  /* A rising fifth on the mallet with a quiet sparkle an octave up - over in
     well under a second, because a message is an interruption. */
  message: {
    length: 1.5,
    volume: 0.5,
    tone: 7500,
    room: 0.16,
    notes: [m(0, 79, 0.9, -0.1), m(0.095, 86, 0.78, 0.1), g(0.095, 98, 0.07, 0.15)],
  },
  /* A falling cadence resolving to the tonic, warm and low: the call is over. */
  callEnded: {
    length: 1.8,
    volume: 0.45,
    tone: 4200,
    room: 0.2,
    notes: [m(0, 76, 0.8, 0.08), m(0.14, 72, 0.78, -0.08), b(0.14, 60, 0.1)],
  },
  join: { length: 1.4, volume: 0.32, tone: 6500, room: 0.14, notes: [m(0, 72, 0.8), m(0.08, 79, 0.75)] },
  leave: { length: 1.4, volume: 0.28, tone: 5200, room: 0.14, notes: [m(0, 79, 0.75), m(0.08, 72, 0.7)] },
  /* What the CALLER hears while the other side rings: two soft bell tones and
     a long rest, so it reads as "ringing" and never as an alarm. */
  ringback: {
    length: 3,
    volume: 0.26,
    tone: 3800,
    room: 0.18,
    notes: [b(0, 76, 0.5), b(0, 64, 0.16), b(0.42, 76, 0.42), b(0.42, 64, 0.13)],
  },
};

/* The ringtones - the ids are stored in each user's call settings, so they are
   kept; only what they sound like changed. */
const RINGTONES: Readonly<Record<string, SoundSpec>> = {
  /* A Cmaj7 arpeggio answered by an Fmaj7 one, each capped by a bell. */
  default: {
    length: 3.4,
    volume: 0.62,
    tone: 8000,
    room: 0.2,
    notes: [
      m(0, 72, 0.7, -0.2), m(0.13, 76, 0.7, -0.07), m(0.26, 79, 0.72, 0.07), m(0.39, 83, 0.7, 0.2),
      b(0.56, 88, 0.36), g(0.56, 100, 0.05),
      m(1.3, 77, 0.7, 0.2), m(1.43, 81, 0.7, 0.07), m(1.56, 84, 0.72, -0.07), m(1.69, 88, 0.7, -0.2),
      b(1.86, 91, 0.34), g(1.86, 103, 0.05),
    ],
  },
  /* Two bells a sixth apart, ringing into each other. */
  chime: {
    length: 3.2,
    volume: 0.55,
    tone: 7000,
    room: 0.24,
    notes: [b(0, 79, 0.6, -0.12), g(0, 91, 0.06), b(0.36, 76, 0.58, 0.12), b(0.36, 64, 0.12)],
  },
  /* The old telephone's double ring, as a soft glass trill rather than a buzzer. */
  classic: {
    length: 3,
    volume: 0.5,
    tone: 6000,
    room: 0.12,
    notes: [
      ...Array.from({ length: 14 }, (_, i) => g(i * 0.055, i % 2 === 0 ? 88 : 84, 0.5, i % 2 === 0 ? -0.1 : 0.1)),
      ...Array.from({ length: 14 }, (_, i) => g(1.05 + i * 0.055, i % 2 === 0 ? 88 : 84, 0.5, i % 2 === 0 ? -0.1 : 0.1)),
    ],
  },
  /* Faster and brighter, for people who asked to be woken. */
  urgent: {
    length: 1.6,
    volume: 0.62,
    tone: 9000,
    room: 0.1,
    notes: [m(0, 84, 0.8), m(0.09, 88, 0.8), m(0.18, 91, 0.8), m(0.46, 84, 0.8), m(0.55, 88, 0.8), m(0.64, 91, 0.85)],
  },
};

export const RINGTONE_IDS: readonly string[] = Object.keys(RINGTONES);

/* ── Rendering ──────────────────────────────────────────────────────────── */

const SAMPLE_RATE = 44_100;
const PEAK = 0.7;
/** Every render ends on silence - a tail cut mid-ring is a click, and in a loop a seam. */
const FADE_OUT_S = 0.06;

const hz = (midi: number): number => 440 * 2 ** ((midi - 69) / 12);

type OfflineCtor = new (channels: number, length: number, sampleRate: number) => OfflineAudioContext;

function offlineCtor(): OfflineCtor | null {
  if (typeof window === 'undefined') return null;
  const scope = window as typeof window & { webkitOfflineAudioContext?: OfflineCtor };
  return (scope.OfflineAudioContext as OfflineCtor | undefined) ?? scope.webkitOfflineAudioContext ?? null;
}

/** A small room: stereo noise under an exponential tail, softened at the top. */
function roomImpulse(ctx: BaseAudioContext, seconds: number): AudioBuffer {
  const length = Math.floor(SAMPLE_RATE * seconds);
  const impulse = ctx.createBuffer(2, length, SAMPLE_RATE);
  for (let channel = 0; channel < 2; channel += 1) {
    const data = impulse.getChannelData(channel);
    let previous = 0;
    for (let i = 0; i < length; i += 1) {
      const t = i / SAMPLE_RATE;
      const noise = Math.random() * 2 - 1;
      /* One-pole low-pass: a real room swallows its highs first. */
      previous = previous * 0.6 + noise * 0.4;
      data[i] = previous * Math.exp(-t * 3.4);
    }
  }
  return impulse;
}

async function render(spec: SoundSpec): Promise<AudioBuffer | null> {
  const Offline = offlineCtor();
  if (Offline === null) return null;
  const ctx = new Offline(2, Math.ceil(SAMPLE_RATE * spec.length), SAMPLE_RATE);

  const bus = ctx.createGain();
  const lowpass = ctx.createBiquadFilter();
  lowpass.type = 'lowpass';
  lowpass.frequency.value = spec.tone ?? 7000;
  lowpass.Q.value = 0.5;
  const dry = ctx.createGain();
  const wet = ctx.createGain();
  wet.gain.value = spec.room ?? 0.15;
  const reverb = ctx.createConvolver();
  reverb.buffer = roomImpulse(ctx, 1.4);
  const compressor = ctx.createDynamicsCompressor();
  compressor.threshold.value = -16;
  compressor.ratio.value = 3;
  compressor.attack.value = 0.003;
  compressor.release.value = 0.2;

  bus.connect(lowpass);
  lowpass.connect(dry).connect(compressor);
  lowpass.connect(reverb).connect(wet).connect(compressor);
  compressor.connect(ctx.destination);

  for (const note of spec.notes) {
    const voice = VOICES[note.voice];
    const panner = ctx.createStereoPanner();
    panner.pan.value = note.pan ?? 0;
    panner.connect(bus);
    const f0 = hz(note.midi);
    for (const partial of voice.partials) {
      const frequency = f0 * partial.ratio;
      if (frequency >= SAMPLE_RATE / 2 - 1000) continue;
      const oscillator = ctx.createOscillator();
      oscillator.type = 'sine';
      oscillator.frequency.value = frequency;
      const envelope = ctx.createGain();
      const start = note.t;
      const peak = note.gain * partial.amp;
      envelope.gain.setValueAtTime(0, start);
      envelope.gain.linearRampToValueAtTime(peak, start + voice.attack);
      envelope.gain.setTargetAtTime(0, start + voice.attack, partial.decay);
      oscillator.connect(envelope).connect(panner);
      oscillator.start(start);
      oscillator.stop(Math.min(spec.length, start + voice.attack + partial.decay * 7));
    }
  }

  const buffer = await ctx.startRendering();
  /* Normalise, so every sound sits at the same peak and `volume` is the only
     thing that decides how loud it is. */
  let max = 0;
  for (let channel = 0; channel < buffer.numberOfChannels; channel += 1) {
    const data = buffer.getChannelData(channel);
    for (let i = 0; i < data.length; i += 1) max = Math.max(max, Math.abs(data[i] ?? 0));
  }
  const scale = max > 0 ? PEAK / max : 1;
  const fadeFrom = Math.max(0, buffer.length - Math.floor(SAMPLE_RATE * FADE_OUT_S));
  const fadeLength = buffer.length - fadeFrom;
  for (let channel = 0; channel < buffer.numberOfChannels; channel += 1) {
    const data = buffer.getChannelData(channel);
    for (let i = 0; i < data.length; i += 1) {
      const fade = i < fadeFrom ? 1 : (buffer.length - i) / fadeLength;
      data[i] = (data[i] ?? 0) * scale * fade;
    }
  }
  return buffer;
}

const rendered = new Map<SoundSpec, Promise<AudioBuffer | null>>();

function bufferFor(spec: SoundSpec): Promise<AudioBuffer | null> {
  let pending = rendered.get(spec);
  if (pending === undefined) {
    pending = render(spec).catch(() => null);
    rendered.set(spec, pending);
  }
  return pending;
}

/* ── Playing ────────────────────────────────────────────────────────────── */

/** Plays a one-shot sound. Silent where there is no Web Audio. */
export function playSound(id: SoundId): void {
  const ctx = getAudioContext();
  if (ctx === null) return;
  resumeAudio();
  const spec = SOUNDS[id];
  void bufferFor(spec).then((buffer) => {
    if (buffer === null) return;
    const source = ctx.createBufferSource();
    source.buffer = buffer;
    const level = ctx.createGain();
    level.gain.value = spec.volume;
    source.connect(level).connect(ctx.destination);
    source.start();
  });
}

/**
 * Loops a sound until the returned function is called, which fades it out
 * rather than cutting it mid-note.
 */
function loop(spec: SoundSpec): () => void {
  const ctx = getAudioContext();
  if (ctx === null) return () => undefined;
  resumeAudio();
  let stopped = false;
  let source: AudioBufferSourceNode | null = null;
  let level: GainNode | null = null;
  void bufferFor(spec).then((buffer) => {
    if (stopped || buffer === null) return;
    source = ctx.createBufferSource();
    source.buffer = buffer;
    source.loop = true;
    level = ctx.createGain();
    level.gain.value = spec.volume;
    source.connect(level).connect(ctx.destination);
    source.start();
  });
  return () => {
    if (stopped) return;
    stopped = true;
    if (source === null || level === null) return;
    const now = ctx.currentTime;
    level.gain.setValueAtTime(level.gain.value, now);
    level.gain.linearRampToValueAtTime(0, now + 0.18);
    source.stop(now + 0.2);
  };
}

/** The incoming-call ringtone, by the id stored in the user's call settings. */
export function startRingtone(id: string): () => void {
  return loop(RINGTONES[id] ?? RINGTONES['default'] ?? SOUNDS.ringback);
}

/** What the caller hears while the other side is being rung. */
export function startRingback(): () => void {
  return loop(SOUNDS.ringback);
}

/**
 * Renders everything ahead of need, so the first message after a page load
 * does not wait on a render. Call from a user gesture or idle time.
 */
export function primeSounds(): void {
  for (const spec of Object.values(SOUNDS)) void bufferFor(spec);
  const ring = RINGTONES['default'];
  if (ring !== undefined) void bufferFor(ring);
}

/* Rendering needs no gesture (an offline context is not subject to autoplay),
   but it is work, so it waits for the first sign somebody is here. */
if (typeof window !== 'undefined') {
  window.addEventListener('pointerdown', primeSounds, { once: true, passive: true });
  window.addEventListener('keydown', primeSounds, { once: true });
}
